import authService from './authService';
import {
  CarrierServiceOption,
  CompanyCarrierBlock,
  PaymentRoutingConfigPayload,
  PaymentRoutingConfigResult,
  PaymentRoutingDecisionPage,
  PaymentRoutingDecisionQuery,
  SimulateRoutingPayload,
  SimulateRoutingResult,
} from '../types/paymentRouting';
import { ApiResponse } from '../types/common';

class PaymentRoutingService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  /** Busca a configuração atual de roteamento (regras + avisos). */
  async getConfig(): Promise<PaymentRoutingConfigResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/paymenthub/payment-routing');
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao buscar configuração de roteamento de pagamentos');
    }
    const data: ApiResponse<PaymentRoutingConfigResult> = await response.json();
    return data.data!;
  }

  /** Salva a configuração completa (substitui todas as regras). */
  async updateConfig(payload: PaymentRoutingConfigPayload): Promise<PaymentRoutingConfigResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put('/paymenthub/payment-routing', payload);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao salvar configuração de roteamento de pagamentos');
    }
    const data: ApiResponse<PaymentRoutingConfigResult> = await response.json();
    return data.data!;
  }

  /** Solicita ao PaymentHub que recarregue o cache de roteamento. */
  async reloadCache(): Promise<string> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/paymenthub/payment-routing/reload');
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao recarregar o cache do PaymentHub');
    }
    const data: ApiResponse<{ message: string }> = await response.json();
    return data.data?.message ?? 'Cache recarregado com sucesso';
  }

  /** Limpa a marcação de teto atingido de uma empresa (reativa o roteamento para ela). */
  async clearCap(companyId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/paymenthub/payment-routing/companies/${companyId}/clear-cap`);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao reativar a empresa');
    }
  }

  /** Transportadoras × serviços para a lista de bloqueio (com rótulo do cliente e uso em 90 dias). */
  async getCarrierServices(): Promise<CarrierServiceOption[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/paymenthub/payment-routing/carrier-services');
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao buscar transportadoras e serviços');
    }
    const data: ApiResponse<CarrierServiceOption[]> = await response.json();
    return data.data ?? [];
  }

  /** Simula para qual empresa um pagamento iria com a configuração e o capturado atuais. */
  async simulate(payload: SimulateRoutingPayload): Promise<SimulateRoutingResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/paymenthub/payment-routing/simulate', payload);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao simular o roteamento');
    }
    const data: ApiResponse<SimulateRoutingResult> = await response.json();
    return data.data!;
  }

  /** Decisões de roteamento registradas pelo PaymentHub (modo balanceamento por peso). */
  async getDecisions(query: PaymentRoutingDecisionQuery): Promise<PaymentRoutingDecisionPage> {
    const apiClient = await this.getApiClient();
    const params = new URLSearchParams({
      from: query.from,
      to: query.to,
      page: String(query.page),
      page_size: String(query.page_size),
    });
    if (query.company_id) params.set('company_id', String(query.company_id));
    const response = await apiClient.get(`/paymenthub/payment-routing/decisions?${params.toString()}`);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao buscar as decisões de roteamento');
    }
    const data: ApiResponse<PaymentRoutingDecisionPage> = await response.json();
    return data.data ?? { items: [], total: 0, page: query.page, page_size: query.page_size };
  }

  /** Transportadoras/serviços bloqueados para a empresa (telas de logística). */
  async getCarrierBlocks(companyId: number): Promise<CompanyCarrierBlock[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/shipping/carrier-blocks?company_id=${companyId}`);
    if (!response.ok) {
      const err = await response.json().catch(() => null);
      throw new Error(err?.message || 'Falha ao buscar bloqueios de transportadora');
    }
    const data: ApiResponse<CompanyCarrierBlock[]> = await response.json();
    return data.data ?? [];
  }
}

export const paymentRoutingService = new PaymentRoutingService();
export default paymentRoutingService;
